
import { useRef } from "react";
import { Link } from "react-router";
import { useLocation, useNavigate } from "react-router";
import {
  Home,
  Plus,
  Camera,
  Receipt,
  LogOut,
  Wallet,
  TrendingUp,
  PieChart,
  Settings,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { setPendingCapture } from "@/lib/pending-capture";
import { logout } from "@/lib/logout";

// Desktop chrome only — hidden below lg, where BottomNav takes over. Fixed to
// the viewport so pages just reserve space with lg:pl-64.
export function SideNav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const cameraRef = useRef<HTMLInputElement>(null);

  function onCapture(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPendingCapture(file);
    navigate("/capture");
  }

  const item = (active: boolean) =>
    cn(
      "group relative flex items-center gap-3 rounded-2xl px-3.5 py-2.5 text-sm font-medium transition-colors",
      active
        ? "bg-primary text-primary-foreground shadow-sm"
        : "text-muted-foreground hover:bg-muted hover:text-foreground",
    );

  return (
    <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-border/70 bg-card/60 px-4 py-6 backdrop-blur-xl lg:flex">
      <Link to="/" className="mb-8 flex items-center gap-2.5 px-2">
        <span className="flex size-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#FF8A5B] to-[#F0492F] text-primary-foreground shadow-md shadow-orange-500/25">
          <Wallet className="size-5" />
        </span>
        <span className="text-lg font-bold tracking-tight">Expenses</span>
      </Link>

      <nav className="flex flex-col gap-1.5">
        <Link to="/" className={item(pathname === "/")}>
          <Home className="size-5" />
          Overview
        </Link>
        <Link
          to="/transactions"
          className={item(pathname === "/transactions")}
        >
          <Receipt className="size-5" />
          Transactions
        </Link>
        <Link
          to="/earnings"
          className={item(pathname.startsWith("/earnings"))}
        >
          <TrendingUp className="size-5" />
          Earnings
        </Link>
        <Link to="/analytics" className={item(pathname === "/analytics")}>
          <PieChart className="size-5" />
          Analytics
        </Link>

        {/* Opens the camera from the real tap gesture; the photo is handed to
            /capture to read. */}
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={onCapture}
        />
        <button
          type="button"
          onClick={() => cameraRef.current?.click()}
          className={item(pathname === "/capture")}
        >
          <Camera className="size-5" />
          Scan receipt
        </button>
      </nav>

      <Link
        to="/add"
        className="mt-4 flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-br from-[#FF8A5B] to-[#F0492F] px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-500/25 transition-transform hover:scale-[1.02] active:scale-[0.99]"
      >
        <Plus className="size-5" strokeWidth={2.5} />
        Add expense
      </Link>

      <Link
        to="/settings"
        className="mt-auto flex items-center gap-3 rounded-2xl px-3.5 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <Settings className="size-5" />
        Settings
      </Link>

      <button
        type="button"
        onClick={() => void logout()}
        className="flex items-center gap-3 rounded-2xl px-3.5 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <LogOut className="size-5" />
        Log out
      </button>
    </aside>
  );
}
